import { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { selectPolicyTypes } from '../../selectors/application';
import { setLocationValidationError } from '../../actions/application';
import { LocationFormCont, LocationTypeCont, LocationTypeButton } from './styles/locationEditor';
import {
  LabelLarge,
  FormSection,
  SectionTitle,
  NumberInput,
  InputErrorMessage,
  LabelWithToolTipCont,
  InputWithToolTip,
} from '../../elements';
import SmoothCollapse from 'react-smooth-collapse';
import MultiRow from '../Shared/MultiRow';
import ToolTipIcon from '../Shared/ToolTipIcon';
import SectionDivider from '../Shared/SectionDivider';
import YesNoQuestion from '../Shared/YesNoQuestion';
import { POLICY_OPTIONS, PROPERTY_LABELS, TOOL_TIP_CONTENT_KEY } from '../../constants';
import useGetApplicationOptions from '../../hooks/useGetApplicationOptions';
import { getBuildingLimitErrorMessage } from '../../helpers/locationLimitErrors';
import { debounce } from 'lodash';
import { NumberFormatValues } from 'react-number-format';
import { TypesWithBuildingLimit, LocationTypeOptions } from '../../types/enums';
import { DesktopAndTablet } from '../../helpers/responsive';
import toolTipContent from '../../helpers/toolTipContent';
import { formatCurrency } from '../../helpers/formatNumber';
import lightbulbIcon from '../../icons/lightbulb.svg';
import styled from 'styled-components';

const CoastalHelperText = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 10px 10px 10px;
  font-family: ${(props) => props.theme.font.typeface.secondary};
  font-size: 14px;
  font-weight: 500;
  line-height: 20px;
  color: ${({ theme }) => theme.colors.tertiary.zero};
`;

interface LocationEditorProps {
  updateLocation: (data: Partial<Location>) => void;
  updateBPPDeductible: (setBppDeductible: number) => void;
  bppDeductible?: number;
  bppLimit?: number;
  buildingLimit?: number;
  expanded?: Location;
  locationType?: string;
  hasSprinklers?: boolean;
  isLoading: boolean;
}

interface BppLimitError {
  value?: number;
  message: string;
}

interface BuildingLimitError {
  value?: number;
  message: string;
}

const locationTypeOptions = Object.keys(LocationTypeOptions) as (keyof typeof LocationTypeOptions)[];

const LocationEditor = ({
  updateLocation,
  updateBPPDeductible,
  bppDeductible,
  bppLimit,
  buildingLimit,
  expanded,
  locationType,
  hasSprinklers,
  isLoading,
}: LocationEditorProps) => {
  const dispatch = useDispatch();
  const applicationTypes = useSelector(selectPolicyTypes);
  const [bppLimitError, setBppLimitError] = useState<BppLimitError | null>(null);
  const [buildingLimitError, setBuildingLimitError] = useState<BuildingLimitError | null>(null);

  const {
    isFetching: isLoadingApplicationOptions,
    isAwaitingOptions,
    data: applicationOptions,
    isError,
  } = useGetApplicationOptions();

  const isMissingOptionsData =
    !isLoadingApplicationOptions &&
    !isError &&
    (applicationOptions?.bppLimit?.max == null ||
      applicationOptions?.bppLimit?.min == null ||
      !applicationOptions?.bppDeductible?.allowedValues?.length);

  const isBop = applicationTypes.includes('BOP');

  useEffect(() => {
    dispatch(
      setLocationValidationError(
        isBop && Boolean(bppLimitError || buildingLimitError || isError || isMissingOptionsData)
      )
    );
  }, [isBop, bppLimitError, buildingLimitError, dispatch, isError, isMissingOptionsData]);

  const propertyClaimsOptions = useMemo(() => {
    return applicationOptions?.bppDeductible?.allowedValues ?? [];
  }, [applicationOptions?.bppDeductible?.allowedValues]);

  useEffect(() => {
    if (!applicationTypes.includes('BOP')) return;
    if (propertyClaimsOptions.length === 1 && bppDeductible !== propertyClaimsOptions[0]) {
      updateBPPDeductible(propertyClaimsOptions[0]);
    }
  }, [propertyClaimsOptions, bppDeductible, updateBPPDeductible, applicationTypes]);

  if (isLoadingApplicationOptions || isAwaitingOptions) return null;

  const defaultBuildingLimit = applicationOptions?.buildingLimit?.default;

  const hasCoastalDeductibleRestriction = applicationOptions?.bppDeductible?.hasCoastalRestriction;
  const isSingleDeductibleOption = propertyClaimsOptions.length === 1;

  const addSpaces = (string: string) => string.replace(/([A-Z])/g, ' $1').trim();

  const updateLocationType = (type: string) => {
    if (type in TypesWithBuildingLimit) {
      updateLocation({ locationType: type, buildingLimit: defaultBuildingLimit });
    } else {
      updateLocation({ locationType: type, buildingLimit: 0 });
    }
  };

  const bppLimitMax = applicationOptions?.bppLimit?.max;
  const bppLimitMin = applicationOptions?.bppLimit?.min;
  const hasCoastalBppRestriction = applicationOptions?.bppLimit?.hasCoastalRestriction;
  const buildingLimitMax = applicationOptions?.buildingLimit?.max;
  const buildingLimitMin = applicationOptions?.buildingLimit?.min;

  const updateBppLimit = debounce((value: NumberFormatValues) => {
    if (value.floatValue == null) return;
    if (bppLimitMax && value.floatValue > bppLimitMax) {
      const message = hasCoastalBppRestriction
        ? `Coverage must be below ${formatCurrency(bppLimitMax)} due to proximity to the coastline`
        : `Coverage must be less than ${formatCurrency(bppLimitMax)}`;
      setBppLimitError({ value: value.floatValue, message });
    } else if (bppLimitMin && value.floatValue < bppLimitMin) {
      const message = `Coverage must be at least ${formatCurrency(bppLimitMin)}`;
      setBppLimitError({ value: value.floatValue, message });
    } else {
      setBppLimitError(null);
      updateLocation({ bppLimit: value.floatValue });
    }
  }, 500);

  const updateBuildingLimit = debounce((value: NumberFormatValues) => {
    if (value.floatValue == null) return;
    const message = getBuildingLimitErrorMessage(value.floatValue, buildingLimitMin, buildingLimitMax);

    if (message) {
      setBuildingLimitError({ value: value.floatValue, message });
    } else {
      setBuildingLimitError(null);
      updateLocation({ buildingLimit: value.floatValue });
    }
  }, 500);

  const updateFireSuppression = (value: boolean) => {
    updateLocation({ hasSprinklers: value });
  };

  const isExpanded = Boolean(expanded && Object.keys(expanded).length > 0);

  return (
    <LocationFormCont expanded={isExpanded} data-cy="location-editor">
      <SmoothCollapse heightTransition=".5s ease" expanded={isExpanded} allowOverflowWhenOpen={true}>
        <LabelLarge>In what type of building do you operate your business?</LabelLarge>
        <LocationTypeCont>
          {locationTypeOptions.map((type) => (
            <LocationTypeButton
              key={type}
              active={type === locationType}
              onClick={() => updateLocationType(type)}
              data-cy={`location-type-${type}`}
              aria-label={`${type} location type`}
              type="button"
            >
              {addSpaces(type)}
            </LocationTypeButton>
          ))}
        </LocationTypeCont>
        <YesNoQuestion
          question="Does it have an automatic fire suppression system?"
          value={hasSprinklers}
          selectYes={() => updateFireSuppression(true)}
          selectNo={() => updateFireSuppression(false)}
          cyLabel="sprinkler-system"
        />
        <SectionDivider />
        <SectionTitle>Property Coverages</SectionTitle>
        <FormSection smaller={true}>
          <LabelWithToolTipCont id="propertyDeductible">
            <LabelLarge display="inline" data-cy="property-deductible">
              For property claims, I want to pay a deductible of
            </LabelLarge>
            <ToolTipIcon
              content={toolTipContent[TOOL_TIP_CONTENT_KEY[POLICY_OPTIONS.PROPERTY_CLAIMS_DEDUCTIBLE]]}
              labelElementId="propertyDeductible"
              toolTipName="Property_Claims_Deductible"
            />
          </LabelWithToolTipCont>
          {hasCoastalDeductibleRestriction && (
            <CoastalHelperText>
              <img src={lightbulbIcon} alt="" width={14} height={14} />
              Only a {formatCurrency(propertyClaimsOptions[0])} deductible is available due to proximity to the
              coastline
            </CoastalHelperText>
          )}
          {propertyClaimsOptions.length > 3 ? (
            <MultiRow
              money={true}
              options={propertyClaimsOptions}
              value={bppDeductible}
              updateValue={(value) => updateBPPDeductible(value)}
              label={PROPERTY_LABELS[POLICY_OPTIONS.PROPERTY_CLAIMS_DEDUCTIBLE]}
            />
          ) : (
            <LocationTypeCont>
              {propertyClaimsOptions.map((propertyClaimsOption) => (
                <LocationTypeButton
                  key={propertyClaimsOption}
                  active={propertyClaimsOption === bppDeductible}
                  onClick={() => !isSingleDeductibleOption && updateBPPDeductible(propertyClaimsOption)}
                  data-cy={`property-claims-${propertyClaimsOption}`}
                  aria-label={`${PROPERTY_LABELS[POLICY_OPTIONS.PROPERTY_CLAIMS_DEDUCTIBLE]}: ${propertyClaimsOption}`}
                  type="button"
                >
                  {formatCurrency(propertyClaimsOption)}
                </LocationTypeButton>
              ))}
            </LocationTypeCont>
          )}
        </FormSection>
        <FormSection smaller={true}>
          <LabelLarge display="inline">For my business property and equipment, I'd like to be covered up to</LabelLarge>
          <DesktopAndTablet>
            <ToolTipIcon
              content={toolTipContent[TOOL_TIP_CONTENT_KEY[POLICY_OPTIONS.BUSINESS_PERSONAL_PROPERTY_LIMIT]]}
              toolTipName="Business_Personal_Property_Limit"
            />
          </DesktopAndTablet>
          <InputWithToolTip
            toolTipContentKey={TOOL_TIP_CONTENT_KEY[POLICY_OPTIONS.BUSINESS_PERSONAL_PROPERTY_LIMIT]}
            toolTipName="Business_Personal_Property_Limit"
            labelElementId="bppLimitInput"
            marginSmall={Boolean(bppLimitError)}
          >
            <NumberInput
              value={bppLimitError?.value ? bppLimitError.value : bppLimit}
              onValueChange={updateBppLimit}
              customwidth="quarter"
              marginbottom="none"
              thousandSeparator={true}
              prefix="$"
              aria-label={PROPERTY_LABELS[POLICY_OPTIONS.BUSINESS_PERSONAL_PROPERTY_LIMIT]}
              data-cy="bppLimit"
              disabled={isLoading}
              hasError={Boolean(bppLimitError)}
              placeholder="e.g., $150,000"
              id="bppLimitInput"
            />
          </InputWithToolTip>
          {bppLimitError && <InputErrorMessage>{bppLimitError.message}</InputErrorMessage>}
        </FormSection>
        <SmoothCollapse
          expanded={Boolean(locationType && locationType in TypesWithBuildingLimit)}
          allowOverflowWhenOpen={true}
        >
          <FormSection smaller={true}>
            <LabelLarge display="inline">For my building, I would like to be covered up to</LabelLarge>
            <DesktopAndTablet>
              <ToolTipIcon
                content={toolTipContent[TOOL_TIP_CONTENT_KEY[POLICY_OPTIONS.BUILDING_LIMIT]]}
                toolTipName="Building_Limit"
              />
            </DesktopAndTablet>
            <InputWithToolTip
              toolTipContentKey={TOOL_TIP_CONTENT_KEY[POLICY_OPTIONS.BUILDING_LIMIT]}
              toolTipName="Building_Limit"
              labelElementId="buildingLimitInput"
              marginSmall={Boolean(buildingLimitError)}
            >
              <NumberInput
                value={buildingLimitError?.value ? buildingLimitError.value : buildingLimit}
                onValueChange={updateBuildingLimit}
                customwidth="quarter"
                marginbottom="none"
                thousandSeparator={true}
                prefix="$"
                aria-label={PROPERTY_LABELS[POLICY_OPTIONS.BUILDING_LIMIT]}
                data-cy="buildingLimit"
                disabled={isLoading}
                hasError={Boolean(buildingLimitError)}
                placeholder="e.g., $150,000"
                id="buildingLimitInput"
              />
            </InputWithToolTip>
            {buildingLimitError && <InputErrorMessage>{buildingLimitError.message}</InputErrorMessage>}
          </FormSection>
        </SmoothCollapse>
      </SmoothCollapse>
    </LocationFormCont>
  );
};

export default LocationEditor;
